import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import {
  Alert, Box, Button, Chip, Dialog, DialogActions, DialogContent, DialogTitle,
  MenuItem, Paper, Stack, Table, TableBody, TableCell, TableHead, TableRow,
  TextField, Tooltip, Typography,
} from "@mui/material";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";

import { ApiError, api } from "../api/client";
import type {
  ContractTemplate, TemplateCheck, TemplateField,
} from "../api/types";
import { useAuth } from "../auth/AuthContext";
import { ile } from "../lib/polski";

/**
 * Wzory umów: wgranie, sprawdzenie i słownik pól.
 *
 * **Sprawdzenie przy wgraniu, nie przy generowaniu** (§4.5). Literówka
 * w nazwie pola nie wywraca renderu — zostawia w umowie puste miejsce po
 * kwocie. Dokument jest wtedy poprawny, wygenerowany, podpisany
 * i bezwartościowy. Dlatego szablon z zarzutami nie ląduje w bazie nawet jako
 * nieczynny, a osoba układająca go w Wordzie może sprawdzać plik dowolnie
 * wiele razy bez zapisywania.
 *
 * **Słownik pól stoi obok**, bo to jest jedyna rzecz, którą trzeba znać, żeby
 * napisać wzór. Trzymanie go wyłącznie w dokumentacji kończy się szablonem
 * z polem, którego nie ma.
 */

const FORMY = [
  { value: "zlecenie", label: "Umowa zlecenia" },
  { value: "dzielo", label: "Umowa o dzieło" },
  { value: "b2b", label: "Współpraca B2B" },
];

const CZESCI = [
  { value: "umowa", label: "Umowa" },
  { value: "oswiadczenie_zus", label: "Oświadczenie o ubezpieczeniach" },
  { value: "oswiadczenie_podatkowe", label: "Oświadczenie podatkowe" },
  { value: "rodo", label: "Klauzula informacyjna RODO" },
];

export function TemplatesPage() {
  const { can } = useAuth();
  const kolejka = useQueryClient();
  const [otwarty, setOtwarty] = useState(false);

  const { data } = useQuery({
    queryKey: ["templates"],
    queryFn: () => api.get<{ results: ContractTemplate[]; fields: TemplateField[] }>(
      "/contract-templates",
    ),
    enabled: can("admin.manage_config"),
  });

  const wycofaj = useMutation({
    mutationFn: (id: string) => api.del(`/contract-templates/${id}`),
    onSuccess: () => kolejka.invalidateQueries({ queryKey: ["templates"] }),
  });

  if (!can("admin.manage_config")) {
    return <Alert severity="info">
      Szablony umów są konfiguracją systemu — wymagają uprawnienia
      administratora.
    </Alert>;
  }
  if (!data) return null;

  const szablony = data.results;
  const czynne = szablony.filter((s) => s.is_active);

  return (
    <Stack spacing={2}>
      <Stack direction="row" alignItems="center" spacing={2}>
        <Typography variant="h6">Wzory umów</Typography>
        <Box sx={{ flex: 1 }} />
        <Button variant="contained" startIcon={<UploadFileIcon />}
                onClick={() => setOtwarty(true)}>
          Wgraj wzór
        </Button>
      </Stack>

      {czynne.length === 0 && (
        <Alert severity="warning">
          Nie ma żadnego czynnego wzoru, więc nie da się wystawić umowy.
          Wgraj plik DOCX z polami ze słownika poniżej.
        </Alert>
      )}

      <Paper variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Nazwa</TableCell>
              <TableCell>Forma współpracy</TableCell>
              <TableCell>Część paczki</TableCell>
              <TableCell>Dołączana</TableCell>
              <TableCell align="right">Umów</TableCell>
              <TableCell>Stan</TableCell>
              <TableCell />
            </TableRow>
          </TableHead>
          <TableBody>
            {szablony.map((s) => (
              <TableRow key={s.id} hover sx={{ opacity: s.is_active ? 1 : 0.55 }}>
                <TableCell>
                  {s.name}
                  <Typography variant="caption" color="text.secondary" display="block">
                    {s.filename} · {ile(s.used_fields.length, "pole", "pola", "pól")}
                  </Typography>
                </TableCell>
                <TableCell>{s.form_label}</TableCell>
                <TableCell>{s.kind_label}</TableCell>
                <TableCell>
                  {s.include_when
                    ? <Chip size="small" variant="outlined" label={`gdy ${s.include_when}`} />
                    : <Typography variant="body2" color="text.secondary">zawsze</Typography>}
                </TableCell>
                <TableCell align="right">{s.contract_count}</TableCell>
                <TableCell>
                  <Chip size="small" variant="outlined"
                        color={s.is_active ? "success" : "default"}
                        label={s.is_active ? "Czynny" : "Wycofany"} />
                </TableCell>
                <TableCell align="right">
                  {s.is_active && (
                    <Tooltip title="Wzór zostaje w bazie — wskazują na niego umowy
                                    już wygenerowane, a dokumentów nie przechowujemy.">
                      <Button size="small" color="error"
                              onClick={() => wycofaj.mutate(s.id)}>
                        Wycofaj
                      </Button>
                    </Tooltip>
                  )}
                </TableCell>
              </TableRow>
            ))}
            {szablony.length === 0 && (
              <TableRow>
                <TableCell colSpan={7}>
                  <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
                    Nie wgrano jeszcze żadnego wzoru.
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </Paper>

      <SlownikPol pola={data.fields} />

      <WgrajWzor open={otwarty} onClose={() => setOtwarty(false)}
                 onZapisano={() => {
                   setOtwarty(false);
                   kolejka.invalidateQueries({ queryKey: ["templates"] });
                 }} />
    </Stack>
  );
}

function SlownikPol({ pola }: { pola: TemplateField[] }) {
  const [pokaz, setPokaz] = useState(false);

  return (
    <Paper variant="outlined">
      <Stack direction="row" alignItems="center" spacing={1}
             sx={{ px: 2, py: 1.25, bgcolor: "action.hover" }}>
        <Typography variant="subtitle2">Pola dostępne w szablonie</Typography>
        <Typography variant="body2" color="text.secondary">
          · {ile(pola.length, "pole", "pola", "pól")} · wstawiaj jako <code>{"{{ nazwa_pola }}"}</code>,
          warunki jako <code>{"{%p if jest_student %}"}</code>
        </Typography>
        <Box sx={{ flex: 1 }} />
        <Button size="small" onClick={() => setPokaz((s) => !s)}>
          {pokaz ? "Zwiń" : "Pokaż"}
        </Button>
      </Stack>
      {pokaz && (
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Nazwa pola</TableCell>
              <TableCell>Co wstawia</TableCell>
              <TableCell>Przykład</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {pola.map((p) => (
              <TableRow key={p.name}>
                <TableCell sx={{ fontFamily: "monospace" }}>
                  {p.name}
                  {p.required && (
                    <Tooltip title="Bez tego pola dokument wygeneruje się poprawnie,
                                    ale nie będzie umową.">
                      <Chip size="small" label="wymagane" color="warning"
                            variant="outlined" sx={{ ml: 1 }} />
                    </Tooltip>
                  )}
                </TableCell>
                <TableCell>{p.label}</TableCell>
                <TableCell>
                  <Typography variant="body2" color="text.secondary">
                    {p.example || "—"}
                  </Typography>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </Paper>
  );
}

function WgrajWzor({ open, onClose, onZapisano }: {
  open: boolean; onClose: () => void; onZapisano: () => void;
}) {
  const [plik, setPlik] = useState<File | null>(null);
  const [form, setForm] = useState({ name: "", form: "zlecenie", kind: "umowa",
                                     include_when: "" });
  const [blad, setBlad] = useState<ApiError | null>(null);

  /** Sprawdzenie bez zapisu: wzór poprawia się kilka razy pod rząd, a wersji
   *  szablonów nie prowadzimy (§4.5). */
  const sprawdz = useMutation({
    mutationFn: () => {
      const dane = new FormData();
      dane.append("file", plik!);
      return api.postForm<TemplateCheck>("/contract-templates/validate", dane);
    },
    onError: (e) => setBlad(e instanceof ApiError ? e : null),
  });

  const zapisz = useMutation({
    mutationFn: () => {
      const dane = new FormData();
      dane.append("file", plik!);
      Object.entries(form).forEach(([k, v]) => v && dane.append(k, v));
      return api.postForm<ContractTemplate>("/contract-templates", dane);
    },
    onSuccess: () => { setBlad(null); onZapisano(); },
    onError: (e) => setBlad(e instanceof ApiError ? e : null),
  });

  function wybierz(nowy: File | null) {
    setPlik(nowy);
    setBlad(null);
    sprawdz.reset();
  }

  const wynik = sprawdz.data;

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>Wgraj wzór umowy</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <Alert severity="info">
            Plik DOCX z polami w podwójnych klamrach. Sprawdzenie wyłapuje
            literówki w nazwach pól i brakujące pola wymagane — <strong>zanim</strong>{" "}
            wzór posłuży do wystawienia czterdziestu umów.
          </Alert>

          <Button component="label" variant="outlined" startIcon={<UploadFileIcon />}>
            {plik ? plik.name : "Wybierz plik DOCX"}
            <input hidden type="file" accept=".docx"
                   onChange={(e) => wybierz(e.target.files?.[0] ?? null)} />
          </Button>

          {plik && (
            <Button onClick={() => sprawdz.mutate()} disabled={sprawdz.isPending}>
              Sprawdź wzór
            </Button>
          )}

          {wynik?.ok && (
            <Alert severity="success" icon={<CheckCircleIcon fontSize="inherit" />}>
              Wzór jest poprawny. Wstawia{" "}
              {ile(wynik.used_fields.length, "pole", "pola", "pól")}:{" "}
              {wynik.used_fields.join(", ")}.
            </Alert>
          )}
          {wynik && !wynik.ok && (
            <Alert severity="error">
              Wzór wymaga poprawek:
              <ul style={{ margin: "4px 0 0", paddingLeft: 18 }}>
                {wynik.problems.map((p) => <li key={p.message}>{p.message}</li>)}
              </ul>
            </Alert>
          )}

          <TextField label="Nazwa" value={form.name} fullWidth
                     helperText="Puste = nazwa pliku."
                     onChange={(e) => setForm({ ...form, name: e.target.value })} />
          <TextField select label="Forma współpracy" value={form.form} fullWidth
                     onChange={(e) => setForm({ ...form, form: e.target.value })}>
            {FORMY.map((f) => <MenuItem key={f.value} value={f.value}>{f.label}</MenuItem>)}
          </TextField>
          <TextField select label="Część paczki dokumentów" value={form.kind} fullWidth
                     helperText="Wykonawca dostaje jeden plik — części są jego rozdziałami."
                     onChange={(e) => setForm({ ...form, kind: e.target.value })}>
            {CZESCI.map((c) => <MenuItem key={c.value} value={c.value}>{c.label}</MenuItem>)}
          </TextField>
          {form.kind !== "umowa" && (
            <TextField label="Dołączaj tylko gdy (nazwa pola)" value={form.include_when}
                       fullWidth
                       helperText="Puste = zawsze. Np. koszty_50 dla oświadczenia
                                   o kosztach autorskich — niepodpisane oświadczenie
                                   w komplecie zatrzymuje umowę przed kadrami."
                       onChange={(e) => setForm({ ...form, include_when: e.target.value })} />
          )}

          {blad && (
            <Alert severity="error">
              {blad.message}
              {blad.body.problems.length > 0 && (
                <ul style={{ margin: "4px 0 0", paddingLeft: 18 }}>
                  {blad.body.problems.map((p) => <li key={p}>{p}</li>)}
                </ul>
              )}
            </Alert>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Anuluj</Button>
        <Button variant="contained" disabled={!plik || zapisz.isPending}
                onClick={() => zapisz.mutate()}>
          Wgraj wzór
        </Button>
      </DialogActions>
    </Dialog>
  );
}
